/**
 * Presence Context
 *
 * Émet la localisation et le curseur de l'utilisateur courant, et — pour les
 * admins uniquement — expose la liste des connectés et leurs curseurs.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { isStrictAdmin } from "@/helpers/UserHelpers";
import { useSocket } from "@/hooks/useSocket";
import {
  CursorPayload,
  PresenceEntry,
  SocketEvents,
  SocketNamespaces,
} from "@/interfaces/ISocket";
import { selectCurrentUser } from "@/services/slices/authSlice";
import { labelForRoute } from "@/utils/presenceActivity";
import { throttle } from "@/utils/throttle";

export type ConnectedUser = PresenceEntry;
export type LiveCursor = CursorPayload;

interface PresenceContextValue {
  connectedUsers: ConnectedUser[];
  cursors: LiveCursor[];
  setActivity: (label: string | null) => void;
}

export const PresenceContext = createContext<PresenceContextValue | undefined>(
  undefined
);

export function PresenceProvider({ children }: { children: React.ReactNode }) {
  const user = useSelector(selectCurrentUser);
  const isAdmin = isStrictAdmin(user);
  const location = useLocation();
  // forceNew: au changement de compte, repartir d'un socket neuf (sinon
  // socket.io réutilise le socket caché avec l'ancien userId).
  // reconnectionAttempts Infinity: après un redéploiement de l'API, le client
  // se reconnecte seul et resynchronise la présence sans reload manuel.
  const { socket, connected } = useSocket(SocketNamespaces.PRESENCE, {
    forceNew: true,
    reconnectionAttempts: Infinity,
  });

  const [connectedUsers, setConnectedUsers] = useState<ConnectedUser[]>([]);
  const [cursors, setCursors] = useState<LiveCursor[]>([]);

  const activityRef = useRef<string | null>(null);
  const [activityVersion, setActivityVersion] = useState(0);

  const setActivity = useCallback((label: string | null) => {
    activityRef.current = label;
    setActivityVersion((v) => v + 1);
  }, []);

  // Émettre la localisation à chaque changement de route ou d'activité
  useEffect(() => {
    if (!socket || !connected) return;
    const label = activityRef.current ?? labelForRoute(location.pathname);
    socket.emit(SocketEvents.LOCATION_UPDATE, {
      route: location.pathname,
      label,
    });
  }, [socket, connected, location.pathname, activityVersion]);

  // Émettre la position du curseur (throttlée), suspendue si l'onglet est caché
  useEffect(() => {
    if (!socket || !connected) return;
    const emit = throttle((x: number, y: number) => {
      socket.emit(SocketEvents.CURSOR_MOVE, { x, y });
    }, 50);
    const onMove = (e: MouseEvent) => {
      if (document.hidden) return;
      // Coordonnées relatives au VIEWPORT (pas au contenu) : le curseur reste
      // toujours visible sur la même page, indépendamment de la position de
      // scroll de chacun. (Léger décalage possible si les fenêtres ont des
      // tailles très différentes, cas rare accepté.)
      emit(e.clientX / window.innerWidth, e.clientY / window.innerHeight);
    };
    window.addEventListener("mousemove", onMove);
    return () => {
      emit.cancel();
      window.removeEventListener("mousemove", onMove);
    };
  }, [socket, connected]);

  // Abonnements réservés aux admins
  useEffect(() => {
    if (!socket || !isAdmin) return;

    const onState = (p: { users: ConnectedUser[] }) =>
      setConnectedUsers(p.users);
    const onCursorMove = (c: LiveCursor) =>
      setCursors((prev) => [
        ...prev.filter((x) => x.userId !== c.userId),
        c,
      ]);
    const onCursorLeave = (p: { userId: string }) =>
      setCursors((prev) => prev.filter((x) => x.userId !== p.userId));

    socket.on(SocketEvents.PRESENCE_STATE, onState);
    socket.on(SocketEvents.PRESENCE_UPDATE, onState);
    socket.on(SocketEvents.CURSOR_MOVE, onCursorMove);
    socket.on(SocketEvents.CURSOR_LEAVE, onCursorLeave);

    return () => {
      socket.off(SocketEvents.PRESENCE_STATE, onState);
      socket.off(SocketEvents.PRESENCE_UPDATE, onState);
      socket.off(SocketEvents.CURSOR_MOVE, onCursorMove);
      socket.off(SocketEvents.CURSOR_LEAVE, onCursorLeave);
    };
  }, [socket, isAdmin]);

  // Au changement de page de l'admin, vider les curseurs : ils sont propres à
  // la route et ne sont plus reçus une fois parti (le serveur n'émet
  // `cursor:leave` que quand l'utilisateur observé bouge/part, pas quand
  // l'observateur navigue). Ils se repeupleront via `cursor:move` sur la
  // nouvelle page. Sans ça, un curseur « fantôme » de l'ancienne page persiste.
  useEffect(() => {
    setCursors([]);
  }, [location.pathname]);

  // Fermer la connexion présence au démontage (ex: logout / navigation hors de
  // l'espace authentifié) pour que le serveur émette `disconnect` et retire
  // l'utilisateur de la liste des connectés.
  useEffect(() => {
    return () => {
      socket?.close();
    };
  }, [socket]);

  const value = useMemo(
    () => ({ connectedUsers, cursors, setActivity }),
    [connectedUsers, cursors, setActivity]
  );

  return (
    <PresenceContext.Provider value={value}>
      {children}
    </PresenceContext.Provider>
  );
}

export function usePresence(): PresenceContextValue {
  const ctx = useContext(PresenceContext);
  if (ctx === undefined) {
    throw new Error("usePresence must be used within a PresenceProvider");
  }
  return ctx;
}

export function useSetPresenceActivity(label: string | null) {
  const { setActivity } = usePresence();
  useEffect(() => {
    setActivity(label);
    return () => setActivity(null);
  }, [label, setActivity]);
}
